/**
 * Argent de la caisse : tout en cents entiers (plan, section 1.1).
 *
 * Aucun calcul en virgule flottante : les divisions passent par BigInt et un
 * arrondi explicite. Arrondi retenu partout : au plus proche, la moitie
 * s'eloignant de zero (« demi superieur » pour un montant positif), comme les
 * taxes de Lightspeed sur 50 vraies ventes (vente/__tests__/taxesVentesReelles.test.js :
 * l'echantillon n'a aucun cas exactement a la moitie, il ne distingue donc pas
 * l'arrondi bancaire).
 */

/** Entier sur (Number.isSafeInteger). */
export function estCents(v) {
    return Number.isSafeInteger(v);
}

function versBigInt(v, nom) {
    if (typeof v === 'bigint') return v;
    if (!Number.isSafeInteger(v)) throw new TypeError(`${nom} : entier attendu`);
    return BigInt(v);
}

/**
 * numerateur / denominateur arrondi au plus proche, la moitie s'eloignant de
 * zero. Entiers (Number sur ou BigInt) ; renvoie un BigInt.
 * @param {number|bigint} numerateur
 * @param {number|bigint} denominateur > 0
 * @returns {bigint}
 */
export function diviserArrondi(numerateur, denominateur) {
    const n = versBigInt(numerateur, 'numerateur');
    const d = versBigInt(denominateur, 'denominateur');
    if (d <= BigInt(0)) throw new RangeError('denominateur : strictement positif attendu');
    const negatif = n < BigInt(0);
    const abs = negatif ? -n : n;
    const q = (abs * BigInt(2) + d) / (d * BigInt(2));
    return negatif ? -q : q;
}

/** BigInt -> Number, en refusant ce qui depasse Number.MAX_SAFE_INTEGER. */
export function versNombre(big) {
    const n = Number(big);
    if (!Number.isSafeInteger(n)) throw new RangeError('montant hors limites');
    return n;
}

/**
 * Pourcentage d'un montant : cents x points de base / 10 000, arrondi au cent.
 * @param {number} cents
 * @param {number} pointsDeBase 1000 = 10 %
 */
export function pourcentDe(cents, pointsDeBase) {
    return versNombre(diviserArrondi(versBigInt(cents, 'cents') * versBigInt(pointsDeBase, 'pointsDeBase'), 10000));
}

/**
 * Repartit `total` cents entre des lignes, au prorata de leurs poids, sans
 * perdre ni creer un cent : plus forts restes (a egalite, la premiere ligne).
 * @param {number} total entier >= 0
 * @param {number[]} poids entiers >= 0
 * @returns {number[]} parts entieres, de somme `total`
 */
export function repartirAuProrata(total, poids) {
    if (!Number.isSafeInteger(total) || total < 0) throw new RangeError('total : entier positif attendu');
    if (!Array.isArray(poids) || poids.some((p) => !Number.isSafeInteger(p) || p < 0)) {
        throw new RangeError('poids : entiers positifs attendus');
    }
    const somme = poids.reduce((a, p) => a + BigInt(p), BigInt(0));
    if (somme === BigInt(0)) {
        if (total !== 0) throw new RangeError('rien sur quoi repartir');
        return poids.map(() => 0);
    }
    const t = BigInt(total);
    const parts = poids.map((p) => (t * BigInt(p)) / somme);
    const restes = poids.map((p, i) => ({ i, reste: (t * BigInt(p)) % somme }));
    let manque = t - parts.reduce((a, p) => a + p, BigInt(0));
    restes.sort((a, b) => (a.reste === b.reste ? a.i - b.i : (b.reste > a.reste ? 1 : -1)));
    for (const { i } of restes) {
        if (manque === BigInt(0)) break;
        parts[i] += BigInt(1);
        manque -= BigInt(1);
    }
    return parts.map(versNombre);
}

/**
 * Montant saisi par l'employe (« 12,50 », « 12.5 », « 3 ») -> cents, ou null
 * s'il est mal forme. Au plus 2 decimales, jamais negatif.
 * @param {string} texte
 * @returns {number|null}
 */
export function centsDepuisSaisie(texte) {
    if (typeof texte !== 'string') return null;
    const t = texte.trim().replace(/\s/g, '').replace(',', '.');
    const m = /^(\d{1,7})(?:\.(\d{0,2}))?$/.exec(t);
    if (!m) return null;
    return Number(m[1]) * 100 + Number((m[2] || '').padEnd(2, '0'));
}

/**
 * Pourcentage saisi (« 10 », « 12,5 ») -> points de base (1250), ou null.
 * Au plus 2 decimales, de 0 a 100 %.
 * @param {string} texte
 * @returns {number|null}
 */
export function pointsDeBaseDepuisSaisie(texte) {
    if (typeof texte !== 'string') return null;
    const t = texte.trim().replace(',', '.');
    const m = /^(\d{1,3})(?:\.(\d{0,2}))?$/.exec(t);
    if (!m) return null;
    const pb = Number(m[1]) * 100 + Number((m[2] || '').padEnd(2, '0'));
    return pb <= 10000 ? pb : null;
}

/**
 * Montant decimal exact (chaine « 1.2475 ») -> entier en unites de
 * 10^-decimales dollar, sans flottant. Sert aux montants de Lightspeed, qui
 * ont parfois plus de 2 decimales (promotions reparties).
 * @param {string} texte
 * @param {number} decimales nombre de decimales de l'unite (2 = cents)
 * @returns {bigint}
 */
export function unitesDepuisDecimal(texte, decimales) {
    const m = /^(-?)(\d+)(?:\.(\d+))?$/.exec(typeof texte === 'string' ? texte.trim() : '');
    if (!m) throw new TypeError(`montant decimal mal forme : ${texte}`);
    const fraction = m[3] || '';
    if (fraction.length > decimales) throw new RangeError(`plus de ${decimales} decimales : ${texte}`);
    const valeur = BigInt(m[2] + fraction.padEnd(decimales, '0'));
    return m[1] === '-' ? -valeur : valeur;
}

/**
 * Date ISO 8601 a l'heure locale du poste, avec son decalage
 * (« 2026-11-03T14:22:05.120-05:00 »), comme le journal (plan, section 1.5).
 * @param {Date} date
 */
export function isoLocal(date) {
    const pad = (n, l = 2) => String(Math.abs(n)).padStart(l, '0');
    const decalage = -date.getTimezoneOffset();
    const signe = decalage >= 0 ? '+' : '-';
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
        + `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}.${pad(date.getMilliseconds(), 3)}`
        + `${signe}${pad(Math.trunc(decalage / 60))}:${pad(decalage % 60)}`;
}
